import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Empty } from "antd";
import {
  FacultyCard,
  FilterSelectedTags,
  LoadingListCard,
} from "Explore/Components";
import SuggestedKeywords from "Explore/Components/SuggestedKeywords/SuggestedKeywords";
import {
  getFacetSearchAuthorRelatedKeywords,
  getSearchAuthors,
  getSearchAuthorsGraph,
  filterScholarsWithFTS,
} from "Explore/Services/Author";
import { replaceFtsMatch } from "Explore/Utils/replaceFtsMatch";
import { FiltersFacultyMobile } from "./Components";
import styles from "./FacultyList.module.scss";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import _ from "lodash";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import { BookmarkPlus } from "lucide-react";
import SaveResultsModal from "Explore/Pages/MyLibrary/Components/SavedSearchResultsPanel/SaveResultsModal";
import InfinityPagination, {
  TOTAL_PAGE_PER_FETCH,
} from "Explore/Components/Pagination/InfinityPagination";
import { ParamsFilterKey } from "Config/filterSearchParams";
import FilterFaculty from "./Components/FiltersFaculty";
import useDeviceCheck from "Hooks/useDeviceCheck";
import D3Graph from "Explore/Components/Graph";
import classNames from "classnames";
// import SortEntity from "./Components/SortFaculty";
import CallToLogin from "Explore/Components/CallToLogin";
import FeatureEntities from "Explore/Components/FeatureEntities";
import KeywordClickSnackBar from "Explore/Components/KeywordClickSnackBar";
import SortEntity from "Explore/Components/SortEntity";
import useAuth from "Hooks/useAuth";
import LoginRequiredSection from "Explore/Components/LoginRequired";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useInfinityPagination from "Hooks/useInfinityPagination";

const grid = {
  gutter: [25, 25],
  xs: 1,
  sm: 1,
  md: 1,
  lg: 1,
  xl: 1,
  xxl: 1,
};

const FacultyList = () => {
  const { isAuthLoading, getUserTypeConfigByEntityType } = useAuth();
  const { filter, pagination, updateSearchParams, getPageHref } =
    useFilterAndPagination();
  const { isSmallDesktop, isTablet } = useDeviceCheck();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [graphModalOpen, setGraphModalOpen] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const fetchListFunction = filter[ParamsFilterKey.FULL_TEXT_SEARCH]
    ? filterScholarsWithFTS
    : getSearchAuthors;

  const handleFetchTopFaculty = useCallback(
    async (targetLimit) => {
      const res = await fetchListFunction({
        ...filter,
        fullTextSearchQuery: filter[ParamsFilterKey.FULL_TEXT_SEARCH],
        orderBy: "h_index",
        order: "desc",
        page: 0,
        rowsPerPage: targetLimit,
        numPages: 1,
      });
      return Array.isArray(res) ? res : res?.results || [];
    },
    [fetchListFunction, filter]
  );

  const {
    data: listData,
    currentMaxPage,
    hasNextPage,
    isLoading,
  } = useInfinityPagination({
    fetchFunction: fetchListFunction,
    page: pagination.page,
    filter: {
      ...filter,
      fullTextSearchQuery: filter[ParamsFilterKey.FULL_TEXT_SEARCH],
      orderBy: "h_index",
      order: "desc",
    },
    rowsPerPage: pagination.rowsPerPage,
    setPagination: updateSearchParams,
    errCallback: (e) => {
      if (e?.response?.status === 401) {
        setShowLoginSection(true);
      }
    },
  });

  const [suggestedKeywords, setSuggestedKeywords] = useState({
    data: [],
    isLoading: true,
  });
  const [showLoginSection, setShowLoginSection] = useState(false);
  const scrollToRef = useRef(null);
  const { keyword } = useParams();
  const titleKeyword =
    filter?.[ParamsFilterKey.KEYWORD_QUERY]?.[0] ||
    filter?.[ParamsFilterKey.QUERY]?.[0] ||
    keyword;

  useEffect(() => {
    if (keyword) {
      updateSearchParams({
        [ParamsFilterKey.KEYWORD_QUERY]: [keyword],
      });
    }
  }, [keyword]);

  const { numPagesPerFetch, userConfig } = useMemo(() => {
    const userConfig = getUserTypeConfigByEntityType("FACULTY");

    return {
      numPagesPerFetch: userConfig?.limit?.numPagesPerFetch || 1,
      userConfig,
    };
  }, [getUserTypeConfigByEntityType]);

  const showSort = useMemo(
    () => !!filter[ParamsFilterKey.KEYWORD_QUERY]?.length,
    [filter],
  );

  const pageToCompute = useMemo(() => {
    scrollToRef.current?.scrollIntoView({
      behavior: "smooth",
    });
    return pagination.page - 1;
  }, [pagination.page]);
  // const prevFilters = useRef(filter);

  // const handleSearch = ({ filter: newFilters, data }) => {
  //   const searchFilters = Object.entries(newFilters).reduce(
  //     (acc, [key, value]) => {
  //       if (value !== null && !(Array.isArray(value) && value.length === 0)) {
  //         if (key === "keywordQuery") {
  //           acc[key] = Array.isArray(value) ? value : [value];
  //         } else {
  //           acc[key] = value;
  //         }
  //       }
  //       return acc;
  //     },
  //     {}
  //   );

  //   let pageToFetch = pageToCompute;
  //   for (
  //     let i = pageToCompute;
  //     i < pageToCompute + numPagesPerFetch + 1;
  //     i++
  //   ) {
  //     pageToFetch = i;
  //     if (!data[i]) {
  //       break;
  //     }
  //   }
  //   const numberOfPageToFetch =
  //     pageToCompute + numPagesPerFetch - pageToFetch;

  //   if (!!numberOfPageToFetch) {
  //     setIsLoading(pageToFetch === pageToCompute);

  //     const ftsQuery = searchFilters[ParamsFilterKey.FULL_TEXT_SEARCH];
  //     // Strip both keys from restFilters to avoid passing them raw
  //     const {
  //       [ParamsFilterKey.FULL_TEXT_SEARCH]: _fts,
  //       ...restFilters
  //     } = searchFilters;

  //     if (ftsQuery) {
  //       // Use full-text search API
  //       filterScholarsWithFTS({
  //         page: pageToFetch,
  //         rowsPerPage: pagination.rowsPerPage,
  //         fullTextSearchQuery: ftsQuery,
  //         ...restFilters
  //       })
  //         .then((res) => {
  //           const processedResults = replaceFtsMatch(res?.results || []);
  //           const newData = { ...data };
  //           let i = 0;
  //           for (; i < numberOfPageToFetch; i++) {
  //             const pageData = processedResults.slice(
  //               i * pagination.rowsPerPage,
  //               (i + 1) * pagination.rowsPerPage
  //             );
  //             if (pageData.length > 0) {
  //               newData[pageToFetch + i] = pageData;
  //             } else {
  //               break;
  //             }
  //           }
  //           setCurrentMaxPage((prev) => Math.max(prev, pageToFetch + i));
  //           setFaculties(newData);
  //           setHasNextPage(res?.has_next_page || false);
  //           setIsLoading(false);
  //         })
  //         .catch((err) => {
  //           if (err?.response?.status === 401) {
  //             setShowLoginSection(true);
  //           }
  //           setCurrentMaxPage(pageToFetch + 1);
  //           setHasNextPage(false);
  //           setIsLoading(false);
  //         });
  //     } else {
  //       // Use normal search API
  //       getSearchAuthors(
  //         {
  //           ...searchFilters,
  //           page: pageToFetch,
  //           rowsPerPage: pagination.rowsPerPage,
  //           numPages: numberOfPageToFetch,
  //           orderBy: "h_index",
  //           order: "desc"
  //         },
  //         (res) => {
  //           const newData = { ...data };
  //           let i = 0;
  //           for (; i < numberOfPageToFetch; i++) {
  //             const pageData = res.results.slice(
  //               i * pagination.rowsPerPage,
  //               (i + 1) * pagination.rowsPerPage
  //             );
  //             if (pageData.length > 0) {
  //               newData[pageToFetch + i] = pageData;
  //             } else {
  //               break;
  //             }
  //           }
  //           setCurrentMaxPage((prev) => Math.max(prev, pageToFetch + i));
  //           setFaculties(newData);
  //           setHasNextPage(res.has_next_page);
  //           setIsLoading(false);
  //         },
  //         (err) => {
  //           if (err?.response?.status === 401) {
  //             setShowLoginSection(true);
  //           }
  //           setCurrentMaxPage(pageToFetch + 1);
  //           setHasNextPage(false);
  //           setIsLoading(false);
  //         }
  //       );
  //     }
  //   }
  // };

  const handleOpenGraphModal = () => {
    setGraphModalOpen(true);
  };

  const handleSuggestedKeywordClick = (keywords) => {
    const filterKeywords = [...(filter[ParamsFilterKey.KEYWORD_QUERY] || [])];
    keywords.forEach((keyword) => {
      if (!filterKeywords.includes(keyword)) {
        filterKeywords.push(keyword);
      }
    });

    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: filterKeywords,
    });
  };

  const onKeywordClick = (keyword) => {
    const _filter = filter?.[[ParamsFilterKey.KEYWORD_QUERY]] || [];
    if (_filter.includes(keyword)) {
      setSnackBarOpen(true);
      return;
    }

    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: [..._filter, keyword],
    });
  };

  // useEffect(() => {
  //   if (!isAuthLoading) {
  //     const newFilter = {
  //       ...filter,
  //     };

  //     let _data = { ...faculties };

  //     if (!_.isEqual(prevFilters.current, newFilter)) {
  //       _data = {};
  //       updateSearchParams({ page: 1 });
  //       setCurrentMaxPage(0);
  //       setHasNextPage(true);
  //     }

  //     handleSearch({ filter, data: _data });

  //     prevFilters.current = newFilter;
  //   }
  // }, [
  //   JSON.stringify(filter),
  //   JSON.stringify(pagination),
  //   isAuthLoading,
  // ]);

  useEffect(() => {
    setSuggestedKeywords({ data: [], isLoading: true });
    getFacetSearchAuthorRelatedKeywords({
      keywordQuery: filter[ParamsFilterKey.KEYWORD_QUERY],
      top_k: 30,
    }).then((res) => {
      setSuggestedKeywords({ data: res, isLoading: false });
    });
  }, [JSON.stringify(filter)]);

  // Re-fetch graph data when filter changes while modal is open
  useEffect(() => {
    if (graphModalOpen) {
      const searchFilters = Object.entries(filter).reduce(
        (acc, [key, value]) => {
          if (value !== null && !(Array.isArray(value) && value.length === 0)) {
            if (key === "keywordQuery") {
              acc[key] = Array.isArray(value) ? value : [value];
            } else {
              acc[key] = value;
            }
          }
          return acc;
        },
        {},
      );
      setGraphLoading(true);
      getSearchAuthorsGraph(searchFilters, (res) => {
        setGraphData(res.data);
        setGraphLoading(false);
      });
    }
  }, [JSON.stringify(filter), graphModalOpen]);

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

  const generateTitle = () => {
    // Advanced title logic (commented out for future version)

    if (!titleKeyword) return "Top Researchers & Faculty | Academic Online";
    const suffix = " | Academic Online";
    const maxLength = 65;

    let titleFull = `Top Researchers in ${titleKeyword}`;
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    let titleShort = `${titleKeyword} Researchers`;
    if ((titleShort + suffix).length <= maxLength) return titleShort + suffix;

    return `${titleKeyword.substring(0, 40)}...${suffix}`;

    // Original simple logic
    // return "Find Top Researchers & Faculty Members | Academic Explorer";
  };

  const generateDescription = () => {
    return titleKeyword
      ? `Find and explore the top researchers and faculty members in ${titleKeyword}.`
      : "Search and discover faculty members at universities across the United States. Find contact information, research interests, publications, and more.";
  };

  return (
    <>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>
      <div className={styles["faculty-list"]}>
        {!isSmallDesktop && <FilterFaculty />}
        <div className={styles["results"]}>
          <div className={styles["results__header"]}>
            <h1 className={styles["results--title"]}>
              Find Faculty at Universities Across the U.S
            </h1>
            <p className={styles["results--description"]}>
              Looking for a{" "}
              <b>
                <em>faculty member</em>
              </b>{" "}
              at a U.S. university? Start your search here to find contact
              information, research interests, and more.
            </p>
          </div>

          <div className={styles["results__body"]}>
            <SuggestedKeywords
              keywords={suggestedKeywords}
              onKeywordClick={handleSuggestedKeywordClick}
            />

            <div className={styles["results--controller"]}>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<BubbleChartIcon />}
                  onClick={handleOpenGraphModal}
                  className={styles["graph-button"]}
                  sx={{
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  View Network Graph
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<BookmarkPlus size={18} />}
                  onClick={() => setSaveModalOpen(true)}
                  className={styles["graph-button"]}
                  disabled={!Array.isArray(listData[pageToCompute]) || listData[pageToCompute].length === 0}
                  sx={{
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Save Search Results
                </Button>
              </div>
              <div className={styles["results--controller__right"]}>
                {isSmallDesktop && <FiltersFacultyMobile />}
                {showSort && <SortEntity />}
              </div>
            </div>

            <FilterSelectedTags
              showLabel
              filters={filter}
              setFilters={updateSearchParams}
            />

            <div className={styles["faculty-list__cards"]} ref={scrollToRef}>
              <LoadingListCard
                isLoading={isLoading}
                grid={grid}
                componentNum={5}
                component={
                  Array.isArray(listData[pageToCompute]) &&
                  listData[pageToCompute].length > 0 ? (
                    <Grid container spacing="8px">
                      {listData[pageToCompute].map((e, i) => (
                        <Grid item xs={12} key={i}>
                          <FacultyCard
                            data={replaceFtsMatch(e)}
                            className={classNames(
                              styles["card"],
                              i % 2 === 0
                                ? styles["even-card"]
                                : styles["odd-card"],
                            )}
                            onKeywordClick={onKeywordClick}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : showLoginSection ? (
                    <LoginRequiredSection />
                  ) : (
                    <Empty />
                  )
                }
              />

              <InfinityPagination
                pagination={pagination}
                setPagination={updateSearchParams}
                sizeChanger={userConfig?.ui_components?.pageSizeChanger}
                pageSizeOptions={[15, 30, 60, 90]}
                currentMaxPage={currentMaxPage}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                addLink
                getPageHref={getPageHref}
                numPagesPerFetch={numPagesPerFetch}
              />
            </div>
          </div>
        </div>

        <div className={styles["right-section"]}>
          {!isTablet && <CallToLogin />}
          <FeatureEntities />
        </div>
      </div>
      <KeywordClickSnackBar open={snackBarOpen} setOpen={setSnackBarOpen} />

      {/* Graph Modal */}
      <Dialog
        open={graphModalOpen}
        onClose={() => setGraphModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "85vh",
            borderRadius: "20px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            background: "#fff",
            color: "#0f3460",
            padding: "28px 24px",
            minHeight: "70px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <BubbleChartIcon sx={{ fontSize: 28 }} />
            <span style={{ fontWeight: 700, fontSize: "1.25rem" }}>
              Faculty Network Graph
            </span>
          </Box>
          <IconButton
            onClick={() => setGraphModalOpen(false)}
            size="small"
            sx={{
              position: "absolute",
              right: 16,
              color: "#0f3460",
              backgroundColor: "rgba(0,0,0,0.05)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            overflow: "hidden",
            height: "100%",
            background: "#fff",
          }}
        >
          <D3Graph
            data={graphData || { nodes: [], links: [] }}
            isLoading={graphLoading}
            graphType="faculty"
          />
        </DialogContent>
      </Dialog>
      <SaveResultsModal
        open={saveModalOpen}
        onOpenChange={setSaveModalOpen}
        entityType="researchers"
        currentItems={Array.isArray(listData[pageToCompute]) ? listData[pageToCompute] : []}
        currentFilters={filter}
        fetchItems={handleFetchTopFaculty}
      />
    </>
  );
};

export default FacultyList;
